import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Heading,
  Image,
  HStack,
  Input,
  FormControl,
  FormLabel,
  IconButton,
  Tooltip,
  SimpleGrid,
  Flex,
  useToast,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Text,
  Divider,
  NumberInput,
  NumberInputField,
  Select,
  VStack,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import {
  HiOutlineArrowNarrowLeft,
  HiOutlinePlus,
  HiOutlineSave,
  HiOutlineTrash,
  HiOutlineUpload
} from "react-icons/hi";
import { MdDragIndicator } from "react-icons/md";
import { debounce, isEqual } from "lodash";
import Navbar from "./Navbar";
import HeroImage from "./images/stamps.png";
import AutoResizeTextarea from "./AutoResizeTextarea";
import CodesetSelect from "./CodesetSelect";
import { useAuthenticatedRequest } from "./hooks/useAuthenticatedRequest";

/* ---------------------------------------
   Helpers
----------------------------------------*/
const safeString = (v) => (typeof v === "string" ? v : v ? String(v) : "");
const tryParseJson = (text) => {
  try {
    return [JSON.parse(text), null];
  } catch (e) {
    return [null, e];
  }
};
const prettyJson = (text) => {
  const [obj, err] = tryParseJson(text);
  if (err || !obj) return text;
  return JSON.stringify(obj, null, 2);
};

const defaultDataDefinition = () => ({
  dataStandard: "",
  dataType: "json",
  schema: "",
  data: "",
});

const PROMPT_TYPES = [
  "role",
  "task",
  "input_format",
  "output_format",
  "general",
  "field",
  "code_field",
  "codeset",
  "array",
  "reasoning",
  "final",
];

const makeUid = () =>
  (typeof window !== "undefined" &&
    window.crypto &&
    typeof window.crypto.randomUUID === "function" &&
    window.crypto.randomUUID()) ||
  `p_${Math.random().toString(36).slice(2)}_${Date.now()}`;

const defaultPrompt = (order = 1) => ({
  uid: makeUid(),
  type: "role",
  order,
  level: 0,
  text: "",
  direction: "",
  dataDefinitions: [],
});

/* ---------------------------------------
   Component
----------------------------------------*/
const CodifyDefinitionsEditor = ({ codifyDefinition, isNew }) => {
  const navigate = useNavigate();
  const toast = useToast();
  const { get, post, put } = useAuthenticatedRequest();

  // Split state: header vs dataDefinitions vs prompts
  const [codifyDefinitionHeader, setCodifyDefinitionHeader] = useState({
    type: "",
    collection: "",
    description: "",
    rolePrompt: "",
    instructionPrompt: "",
    formatPrompt: "",
    generalPrompt: "",
    generalDirection: "",
    fieldPrompt: "",
    fieldDirection: "",
    codeFieldPrompt: "",
    codeFieldDirection: "",
    finalPrompt: "",
  });
  const [dataDefinitions, setDataDefinitions] = useState([]);
  const [prompts, setPrompts] = useState([]);
  const [saving, setSaving] = useState(false);

  // --- Registers state (loaded once) ---
  const [registers, setRegisters] = useState([]);
  const [registersLoading, setRegistersLoading] = useState(false);
  const [registersError, setRegistersError] = useState("");

  const [codesetInputs, setCodesetInputs] = useState({});
  const setCodesetInput = (uid, field, value) =>
    setCodesetInputs((prev) => ({
      ...prev,
      [uid]: { tableId: "", filter: "", ...(prev[uid] || {}), [field]: value },
    }));

  // Ephemeral selection state per Data Definition (keyed by `${uid}:${ddIdx}`)
  const [registerSelections, setRegisterSelections] = useState({});
  const keyFor = (uid, ddIdx) => `${uid}:${ddIdx}`;
  const setRegisterSelection = (uid, ddIdx, field, value) =>
    setRegisterSelections((prev) => ({
      ...prev,
      [keyFor(uid, ddIdx)]: {
        registerId: "",
        entryId: "",
        ...(prev[keyFor(uid, ddIdx)] || {}),
        [field]: value,
      },
    }));

  // Keep a snapshot to compute dirty-state
  const originalRef = useRef({ header: null, dataDefs: null, prompts: null });

  // Load registers once
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setRegistersLoading(true);
        setRegistersError("");
        const res = await get("https://registers.poc.mrc.ai/api/registers");
        const data = res.data;

        // Now fetch entries for each register and attach them
        if (!cancelled && Array.isArray(data)) {
          const registersWithEntries = await Promise.all(
            data.map(async (register) => {
              try {
                const entriesRes = await get(
                  `https://registers.poc.mrc.ai/api/registerentries?registerId=${register._id}`
                );
                const entries = entriesRes.data;
                return { ...register, entries: Array.isArray(entries) ? entries : [] };
              } catch (err) {
                console.error(`Failed to fetch entries for register ${register._id}:`, err);
              }
              return { ...register, entries: [] };
            })
          );
          if (!cancelled) setRegisters(registersWithEntries);
        }
      } catch (e) {
        if (!cancelled)
          setRegistersError(e?.message || "Failed to load registers");
      } finally {
        if (!cancelled) setRegistersLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Fetch and insert a template for a specific Data Definition
  const insertRegisterTemplate = async (uid, ddIdx) => {
    try {
      const sel = registerSelections[keyFor(uid, ddIdx)] || {};
      const registerId = safeString(sel.registerId);
      const entryId = safeString(sel.entryId);

      if (!registerId) {
        toast({ title: "Choose a Register", status: "info", duration: 2500 });
        return;
      }
      if (!entryId) {
        toast({
          title: "Choose a Register Entry",
          status: "info",
          duration: 2500,
        });
        return;
      }

      // Use the new register entries API endpoint
      const url = `https://registers.poc.mrc.ai/api/registerentries/${encodeURIComponent(entryId)}`;

      let bodyText = "";
      let ok = false;

      try {
        const res = await get(url);
        if (typeof res.data === "object") {
          bodyText = JSON.stringify(res.data, null, 2);
        } else {
          bodyText = String(res.data);
        }
        ok = true;
      } catch (err) {
        console.error("Failed to fetch register entry:", err);
      }

      if (!ok) {
        // Fallback: embed a minimal reference marker so you still capture selection
        bodyText = JSON.stringify(
          {
            $registerRef: {
              registerId,
              entryId,
            },
            // Insert your own structure here if desired
          },
          null,
          2
        );
      }

      // Write into the chosen Data Definition's "schema"
      handlePromptDataDefFieldByUid(uid, ddIdx, "schema", bodyText);

      toast({
        title: ok ? "Template inserted" : "Reference inserted (fallback)",
        description: ok
          ? "Loaded template from Registers API."
          : "Couldn’t find a content endpoint — inserted a reference instead.",
        status: ok ? "success" : "warning",
        duration: 3000,
      });
    } catch (err) {
      console.error("❌ Register template fetch error:", err);
      toast({
        title: "Failed to insert template",
        description: err?.message || "Unexpected error calling Registers API.",
        status: "error",
        duration: 5000,
      });
    }
  };

  // ---------------- Code-field helpers ----------------
const CODE_FIELD_PATTERN = /(^code$|^type$|basis$|Code$|Type$|Basis$)/i;

const DEFAULT_AGENCY_NAME = "ACORD";

const keywordLabelMap = [
  { test: /currency/i, label: "ISO4217 Currency Codes" },
  { test: /country/i,  label: "ISO3166-1 Country Codes" },
  { test: /language/i, label: "ISO 639 Language Codes" },

  { test: /state|province|region/i, label: "ISO3166-2 Subdivision Codes" },
];

const guessLabelFromName = (name = "") => {
  for (const k of keywordLabelMap) if (k.test.test(name)) return k.label;
  return ""; // unknown → leave blank; user can edit later
};

const labelFromAgencyCodeset = (propSchema) => {
  const code = safeString(propSchema?.agencyCodeSetCode).trim();
  if (!code) return "";
  const agency = safeString(propSchema?.agencyCode).trim();
  if (!agency) {
    return `Use only the ${code} Codeset values as defined below.`;
  }
  return `Use only the ${agency} ${code} Codeset values as defined below.`;
};

const guessLabelFromNode = (node) => {
  if (!node || typeof node !== "object") return "";
  const agencyLabel = labelFromAgencyCodeset(node);
  if (agencyLabel) return agencyLabel;

  if (typeof node.codesetName === "string") return node.codesetName;
  if (typeof node.codeSetName === "string") return node.codeSetName;
  if (typeof node.codeset === "string") return node.codeset;
  if (node.$codeset && typeof node.$codeset.name === "string") return node.$codeset.name;
  if (node.$registerRef) {
    const r = node.$registerRef;
    if (r.entryId) return `Register Entry ${r.entryId}`;
    if (r.registerId) return `Register ${r.registerId}`;
  }
  if (Array.isArray(node.enum)) return "Enumerated Code List";
  return "";
};

// Build dotted path; arrays show as [] segment
const pathToString = (segments) =>
  segments
    .filter((s) => s !== "properties" && s !== "items") // omit schema meta-keys
    .map((s) => (typeof s === "string" && s.startsWith("[") ? "[]" : s))
    .join(".");

const walkCollectCodeFields = (node, results, path = []) => {
  if (Array.isArray(node)) {
    node.forEach((v, i) => walkCollectCodeFields(v, results, path.concat([`[${i}]`])));
    return;
  }
  if (!node || typeof node !== "object") return;

  // JSON Schema style — properties
  if (node.properties && typeof node.properties === "object") {
    for (const [propName, propSchema] of Object.entries(node.properties)) {
      const nextPath = path.concat(["properties", propName]);
      if (CODE_FIELD_PATTERN.test(propName)) {
        const label =
          labelFromAgencyCodeset(propSchema) ||
          guessLabelFromNode(propSchema) ||
          guessLabelFromName(propName);
        results.push({
          name: propName,
          label,
          path: pathToString(nextPath), // dotted path for this property
        });
      }
      walkCollectCodeFields(propSchema, results, nextPath);
    }
  }

  // JSON Schema style — arrays (items)
  if (node.items) {
    // Append [] segment to indicate array context
    const arrayPath = path.concat(["items", "[]"]);
    walkCollectCodeFields(node.items, results, arrayPath);
  }

  // Generic pass through other keys (avoid re-walking properties/items we handled)
  for (const [k, v] of Object.entries(node)) {
    if (k === "properties" || k === "items") continue;

    const nextPath = path.concat([k]);
    if (CODE_FIELD_PATTERN.test(k)) {
      const label =
        labelFromAgencyCodeset(v) || guessLabelFromNode(v) || guessLabelFromName(k);
      results.push({
        name: k,
        label,
        path: pathToString(nextPath),
      });
    }
    walkCollectCodeFields(v, results, nextPath);
  }
};

const collectFromSchemaText = (schemaText) => {
  const out = [];
  const s = safeString(schemaText).trim();
  if (!s) return out;
  if (!(s.startsWith("{") || s.startsWith("["))) return out; // only JSON payloads
  const [json, err] = tryParseJson(s);
  if (err || !json) return out;
  walkCollectCodeFields(json, out);
  return out;
};

// Gather across all prompts' input/output format schemas
const collectCodeFieldsFromAllSchemas = (allPrompts) => {
  const pairs = [];
  (allPrompts || [])
    .filter((pp) => pp?.type === "input_format" || pp?.type === "output_format")
    .forEach((pp) => {
      (pp.dataDefinitions || []).forEach((dd) => {
        pairs.push(...collectFromSchemaText(dd?.schema));
      });
    });

  // Dedupe by dotted path (so parent[].child and parent.child are distinct)
  // Prefer agency-guided labels when available.
  const byPath = new Map(); // path -> label
  for (const { path, label } of pairs) {
    const current = byPath.get(path);
    const isAgency = label?.includes("Codeset values as defined below.");
    const currentIsAgency = current?.includes?.("Codeset values as defined below.");
    if (!byPath.has(path)) byPath.set(path, label || "");
    else if (!current && label) byPath.set(path, label);
    else if (!currentIsAgency && isAgency) byPath.set(path, label);
  }

  return Array.from(byPath.entries()).map(([path, label]) => ({ path, label }));
};


// --------- Classifications directive helpers ----------
const humanizeType = (t) => {
  const s = safeString(t);
  if (!s) return "";
  // Turn "waters_navigated" -> "waters navigated"
  return s.replace(/_/g, " ");
};

const joinBlocks = (...parts) =>
  parts.filter((s) => !!safeString(s).trim()).join("\n\n");


const collectClassificationsFromData = (dataText) => {
  const lines = [];
  const s = safeString(dataText).trim();
  if (!s) return lines;
  if (!(s.startsWith("{") || s.startsWith("["))) return lines;

  const [json, err] = tryParseJson(s);
  if (err || !json) return lines;

  // Walk arbitrary JSON and collect any *.classifications arrays
  const walk = (node) => {
    if (Array.isArray(node)) {
      node.forEach(walk);
      return;
    }
    if (node && typeof node === "object") {
      // If object has a 'classifications' array, process it
      if (Array.isArray(node.classifications)) {
        node.classifications.forEach((item) => {
          if (!item || typeof item !== "object") return;
          const type = item.type; // e.g., "waters_navigated"
          const agencyCode = safeString(item.agencyCode);
          const codeSet = safeString(item.agencyCodeSetCode);

          if (type && codeSet) {
            const friendly = humanizeType(type);
            // Build exactly the phrasing you asked for:
            // "  - waters_navigated : If waters navigated is provided, set agencyCode to 'ACORD'
            //    and use only the MAR004 Codeset values as defined below."
            lines.push(
              `  - ${type} : If ${friendly} is provided, set agencyCode to '${agencyCode}' and use only the ${codeSet} Codeset values as defined below.`
            );
          }
        });
      }

      // Recurse into all properties
      for (const v of Object.values(node)) walk(v);
    }
  };

  walk(json);

  // De-dupe identical lines
  return Array.from(new Set(lines));
};

/**
 * Scan all prompts for input/output formats and extract directives from any
 * sample data that contains `classifications` arrays.
 * Returns a full block (string) or "" if nothing found.
 */
const buildClassificationsDirectiveBlock = (allPrompts) => {
  const lines = [];
  (allPrompts || [])
    .filter((pp) => pp?.type === "input_format" || pp?.type === "output_format")
    .forEach((pp) => {
      (pp.dataDefinitions || []).forEach((dd) => {
        lines.push(...collectClassificationsFromData(dd?.data));
      });
    });

  const unique = Array.from(new Set(lines));
  if (!unique.length) return "";

  // Wrap with the header you specified
  return [
    ...unique,
  ].join("\n");
};



  // Load incoming prop
  useEffect(() => {
    if (!codifyDefinition) return;

    const {
      dataDefinitions: incomingDataDefs,
      prompts: incomingPrompts,
      ...rest
    } = codifyDefinition;

    const header = {
      type: safeString(rest.type),
      collection: safeString(rest.collection),
      description: safeString(rest.description),
      rolePrompt: safeString(rest.rolePrompt),
      instructionPrompt: safeString(rest.instructionPrompt),
      formatPrompt: safeString(rest.formatPrompt),
      generalPrompt: safeString(rest.generalPrompt),
      generalDirection: safeString(rest.generalDirection),
      fieldPrompt: safeString(rest.fieldPrompt),
      fieldDirection: safeString(rest.fieldDirection),
      codeFieldPrompt: safeString(rest.codeFieldPrompt),
      codeFieldDirection: safeString(rest.codeFieldDirection),
      finalPrompt: safeString(rest.finalPrompt),
      ...rest,
    };

    const defs = (incomingDataDefs || []).map((d) => ({
      dataStandard: safeString(d.dataStandard),
      dataType: safeString(d.dataType || "json"),
      schema: safeString(d.schema),
      data: safeString(d.data),
    }));

    const prmpts = (incomingPrompts || []).map((p) => ({
      uid: p.uid || makeUid(),
      type: safeString(p.type || "role"),
      order:
        typeof p.order === "number"
          ? p.order
          : Number(safeString(p.order)) || 0,
      level:
        typeof p.level === "number"
          ? p.level
          : Number(safeString(p.level)) || 0,
      text: safeString(p.text),
      direction: safeString(p.direction),
      dataDefinitions: (p.dataDefinitions || []).map((d) => ({
        dataStandard: safeString(d.dataStandard),
        dataType: safeString(d.dataType || "json"),
        schema: safeString(d.schema),
        data: safeString(d.data),
      })),
      // ❌ no codesetTableId / codesetFilter here
    }));

    setCodifyDefinitionHeader(header);
    setDataDefinitions(defs);
    setPrompts(prmpts);

    originalRef.current = { header, dataDefs: defs, prompts: prmpts };
  }, [codifyDefinition]);

  // Debounced header field update
  const handleChange = useMemo(() => {
    const fn = (field, value) => {
      setCodifyDefinitionHeader((prev) =>
        prev[field] === value ? prev : { ...prev, [field]: value }
      );
    };
    const debounced = debounce(fn, 50);
    return debounced;
  }, []);
  useEffect(() => () => handleChange.cancel(), [handleChange]);

  /* -----------------------
     Data Definitions CRUD
  ------------------------*/
  const addDataDefinition = () =>
    setDataDefinitions((prev) => [...prev, defaultDataDefinition()]);
  const deleteDataDefinition = (idx) =>
    setDataDefinitions((prev) => prev.filter((_, i) => i !== idx));
  const handleDataDefinitionChange = (idx, field, value) => {
    setDataDefinitions((prev) =>
      prev.map((seg, i) => (i === idx ? { ...seg, [field]: value } : seg))
    );
  };

  /* -----------------------
     Prompts CRUD (uid-based)
  ------------------------*/
  const addPrompt = () =>
    setPrompts((prev) => [...prev, defaultPrompt(prev.length + 1)]);

  const deletePromptByUid = (uid) =>
    setPrompts((prev) => prev.filter((p) => p.uid !== uid));

  const setPromptFieldByUid = (uid, field, value) =>
    setPrompts((prev) =>
      prev.map((p) => (p.uid === uid ? { ...p, [field]: value } : p))
    );

  // Reorder prompts via drag and drop
  const reorderPrompts = (fromIndex, toIndex) => {
    setPrompts((prev) => {
      const sorted = [...prev].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

      // Get all order values in sorted order
      const orderValues = sorted.map(p => p.order);

      // Move the item
      const [movedItem] = sorted.splice(fromIndex, 1);
      sorted.splice(toIndex, 0, movedItem);

      // Reassign order values based on new positions
      return prev.map((p) => {
        const newIndex = sorted.findIndex(sp => sp.uid === p.uid);
        return { ...p, order: orderValues[newIndex] };
      });
    });
  };

  // Nested prompt dataDefinitions (uid-based)
  const addPromptDataDefByUid = (uid) =>
    setPrompts((prev) =>
      prev.map((p) =>
        p.uid === uid
          ? {
              ...p,
              dataDefinitions: [
                ...(p.dataDefinitions || []),
                defaultDataDefinition(),
              ],
            }
          : p
      )
    );

  const deletePromptDataDefByUid = (uid, ddIdx) =>
    setPrompts((prev) =>
      prev.map((p) =>
        p.uid === uid
          ? {
              ...p,
              dataDefinitions: (p.dataDefinitions || []).filter(
                (_, j) => j !== ddIdx
              ),
            }
          : p
      )
    );

  const handlePromptDataDefFieldByUid = (uid, ddIdx, field, value) =>
    setPrompts((prev) =>
      prev.map((p) =>
        p.uid === uid
          ? {
              ...p,
              dataDefinitions: (p.dataDefinitions || []).map((d, j) =>
                j === ddIdx ? { ...d, [field]: value } : d
              ),
            }
          : p
      )
    );

  /* -----------------------
     Dirty, Save, Close
  ------------------------*/
  const isDirty =
    !isEqual(codifyDefinitionHeader, originalRef.current.header) ||
    !isEqual(dataDefinitions, originalRef.current.dataDefs) ||
    !isEqual(prompts, originalRef.current.prompts);

  const handleSave = async () => {
    setSaving(true);
    try {
      if (!safeString(codifyDefinitionHeader.type))
        throw new Error("Type is required.");
      if (!safeString(codifyDefinitionHeader.collection))
        throw new Error("Collection is required.");

      const payload = {
        ...codifyDefinitionHeader,
        dataDefinitions,
        prompts,
      };

      const url = isNew
        ? "https://codify.poc.mrc.ai/api/definitions"
        : `https://codify.poc.mrc.ai/api/definitions/${codifyDefinition._id}`;

      const response = isNew
        ? await post(url, payload)
        : await put(url, payload);

      const result = response.data || {};

      toast({
        title: isNew ? "Codify Definition created" : "Codify Definition saved",
        description: isNew
          ? "New Codify Definition was added."
          : "Changes saved successfully.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      // Reset dirty baseline
      originalRef.current = {
        header: { ...payload },
        dataDefs: [...dataDefinitions],
        prompts: [...prompts],
      };

      if (isNew && result && result._id)
        navigate(`/codifydefinitions/${result._id}`);
    } catch (error) {
      console.error("❌ Error saving codify definition:", error);
      toast({
        title: "Save failed",
        description:
          error?.message ||
          "An error occurred while saving the codify definition.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => navigate(-1);

  // Keyboard: Ctrl/Cmd+S
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "s") {
        e.preventDefault();
        if (!saving) handleSave();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [saving]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sort for display but keep stable uid mapping
  const sortedPromptUids = useMemo(() => {
    return [...prompts]
      .sort(
        (a, b) =>
          (a.order ?? 0) - (b.order ?? 0) || (a.level ?? 0) - (b.level ?? 0)
      )
      .map((p) => p.uid);
  }, [prompts]);

  // Replace your existing fetchCodesetDescriptiveText with this:
  const fetchCodesetDescriptiveText = async (uid, tableIdRaw, filterRaw) => {
    try {
      const tableId = safeString(tableIdRaw).trim();
      const filter = safeString(filterRaw).trim(); // keep as-typed

      if (!tableId) {
        toast({
          title: "Codeset Table ID required",
          description: "Enter the Table ID (e.g., A82) before fetching.",
          status: "info",
          duration: 3000,
        });
        return;
      }

      // Build URL: if filter is blank, no querystring
      const base = `https://codex.mrc.ai/api/codelists/${encodeURIComponent(
        tableId
      )}/descriptivetext`;
      const url = filter
        ? `${base}?filter=${encodeURIComponent(filter)}`
        : base;

      const res = await get(url);

      const body = typeof res.data === "object"
        ? JSON.stringify(res.data, null, 2)
        : String(res.data);

      // Paste result into this prompt's direction
      setPromptFieldByUid(uid, "direction", body);

      toast({
        title: "Codeset values inserted",
        description: `Fetched descriptive text for ${tableId}`,
        status: "success",
        duration: 2500,
      });
    } catch (err) {
      console.error("❌ Codeset fetch error:", err);
      toast({
        title: "Failed to fetch Codeset values",
        description: err?.message || "Unexpected error calling CodexAPI.",
        status: "error",
        duration: 5000,
      });
    }
  };

  const coerceNum = (n) => (Number.isFinite(n) ? n : 0);

  return (
    <Box>
      <Navbar />
      <Box position="relative" width="100%" minHeight="100vh">
        <Image
          src={HeroImage}
          alt="Hero Watermark"
          width="100%"
          height="100%"
          objectFit="cover"
          opacity={0.35}
          position="absolute"
          top={0}
          left={0}
          zIndex={-1}
          filter="blur(1px)"
        />

        <Box position="relative" zIndex={1} p={10}>
          <Box
            borderWidth="1px"
            borderRadius="md"
            p={1}
            mb={1}
            borderColor="gray.500"
            backgroundColor="whiteAlpha.800">
            <Flex mb={1} align="center" justify="space-between">
              <HStack spacing={6}>
                <Tooltip
                  label="Back to the Environment Homepage"
                  aria-label="Back tooltip">
                  <IconButton
                    icon={<HiOutlineArrowNarrowLeft />}
                    size="md"
                    onClick={handleClose}
                    aria-label="Back"
                  />
                </Tooltip>

                <Heading size="lg">Edit Codify Definition</Heading>

                <Tooltip
                  label="Save Codify Definition (Ctrl/Cmd+S)"
                  aria-label="Save tooltip">
                  <IconButton
                    icon={<HiOutlineSave />}
                    size="md"
                    onClick={handleSave}
                    aria-label="Save"
                    isDisabled={saving}
                    isLoading={saving}
                  />
                </Tooltip>

                <Text fontSize="sm" opacity={isDirty ? 1 : 0.6}>
                  {isDirty ? "You have unsaved changes" : "Data is up to date"}
                </Text>
              </HStack>
            </Flex>
          </Box>

          <Box
            borderWidth="1px"
            borderRadius="md"
            p={2}
            mb={2}
            borderColor="gray.500"
            backgroundColor="whiteAlpha.800">
            <Heading fontSize="sm">Definition</Heading>

            <SimpleGrid columns={[1, 2]} spacing={3}>
              <FormControl>
                <FormLabel fontSize="xs">Type</FormLabel>
                <Input
                  fontSize="sm"
                  value={codifyDefinitionHeader.type || ""}
                  onChange={(e) => handleChange("type", e.target.value)}
                />
              </FormControl>

              <CodesetSelect
                size="xs"
                label="Collection"
                codesetCode="CODIFY003"
                value={
                  codifyDefinitionHeader.collection
                    ? {
                        code: codifyDefinitionHeader.collection,
                        description: "",
                      }
                    : null
                }
                onChange={(selected) =>
                  handleChange("collection", selected?.code || "")
                }
              />
            </SimpleGrid>

            <FormControl mt={2}>
              <FormLabel fontSize="xs">Description</FormLabel>
              <AutoResizeTextarea
                fontSize="sm"
                resize="vertical"
                minHeight="30px"
                value={codifyDefinitionHeader.description || ""}
                onChange={(e) => handleChange("description", e.target.value)}
              />
            </FormControl>

            <Divider my={3} />
            <HStack justify="left">
              <Heading fontSize="sm">Directions</Heading>
              <Tooltip
                label="Add Direction..."
                aria-label="Add Direction tooltip">
                <IconButton
                  icon={<HiOutlinePlus />}
                  size="xs"
                  onClick={addPrompt}
                  aria-label="Add Direction"
                />
              </Tooltip>
            </HStack>

            {!prompts.length ? (
              <Text fontSize="sm" opacity={0.7}>
                No directions yet.
              </Text>
            ) : (
              <Box
                borderWidth="1px"
                borderRadius="md"
                p={2}
                mb={2}
                borderColor="gray.500"
                backgroundColor="whiteAlpha.800">
                <Tabs
                  size="sm"
                  orientation="vertical"
                  variant="line"
                  colorScheme="linkedin">
                  <TabList>
                    {sortedPromptUids.map((uid, idx) => {
                      const p = prompts.find((x) => x.uid === uid);
                      return (
                        <HStack
                          key={`prm-tab-wrapper-${uid}`}
                          spacing={1}
                          align="center"
                          onDragOver={(e) => {
                            e.preventDefault();
                            e.dataTransfer.dropEffect = "move";
                          }}
                          onDrop={(e) => {
                            e.preventDefault();
                            const fromIndex = parseInt(e.dataTransfer.getData("text/plain"), 10);
                            const toIndex = idx;
                            if (fromIndex !== toIndex) {
                              reorderPrompts(fromIndex, toIndex);
                            }
                          }}
                          _hover={{ bg: "gray.50" }}
                          borderRadius="md"
                          px={1}>
                          <Box
                            color="gray.400"
                            cursor="grab"
                            _active={{ cursor: "grabbing" }}
                            draggable
                            onDragStart={(e) => {
                              e.dataTransfer.effectAllowed = "move";
                              e.dataTransfer.setData("text/plain", idx.toString());
                            }}>
                            <MdDragIndicator size={16} />
                          </Box>
                          <Tab
                            key={`prm-tab-${uid}`}
                            fontSize="xs"
                            width="max-content">
                            {p?.order ?? 0}. {p?.type || "prompt"}
                          </Tab>
                        </HStack>
                      );
                    })}
                  </TabList>

                  <TabPanels>
                    {sortedPromptUids.map((uid) => {
                      const p = prompts.find((x) => x.uid === uid) || {};
                      return (
                        <TabPanel key={`prm-panel-${uid}`}>
                          {/* Panel header with delete button (avoid nested buttons in Tab label) */}
                          <HStack justify="space-between" mb={3}>
                            <Heading size="sm">
                              {p?.order ?? 0}. {p?.type || "prompt"}
                            </Heading>
                            <Tooltip
                              label="Delete Direction"
                              aria-label="Delete Direction">
                              <IconButton
                                icon={<HiOutlineTrash />}
                                size="xs"
                                aria-label="Delete Direction"
                                onClick={() => {
                                  if (window.confirm("Delete this direction?"))
                                    deletePromptByUid(uid);
                                }}
                              />
                            </Tooltip>
                          </HStack>

                          <SimpleGrid columns={[1, 3]} spacing={3} mb={3}>
                            <FormControl>
                              <FormLabel fontSize="xs">Type</FormLabel>
                              <Select
                                size="sm"
                                value={p.type || "role"}
                                onChange={(e) =>
                                  setPromptFieldByUid(
                                    uid,
                                    "type",
                                    e.target.value
                                  )
                                }>
                                {PROMPT_TYPES.map((t) => (
                                  <option key={t} value={t}>
                                    {t}
                                  </option>
                                ))}
                              </Select>
                            </FormControl>

                            <FormControl>
                              <FormLabel fontSize="xs">Order</FormLabel>
                              <NumberInput
                                size="sm"
                                value={p.order ?? 0}
                                min={0}
                                onChange={(_, valAsNum) =>
                                  setPromptFieldByUid(
                                    uid,
                                    "order",
                                    coerceNum(valAsNum)
                                  )
                                }>
                                <NumberInputField />
                              </NumberInput>
                            </FormControl>

                            <FormControl>
                              <FormLabel fontSize="xs">Level</FormLabel>
                              <NumberInput
                                size="sm"
                                value={p.level ?? 0}
                                min={0}
                                onChange={(_, valAsNum) =>
                                  setPromptFieldByUid(
                                    uid,
                                    "level",
                                    coerceNum(valAsNum)
                                  )
                                }>
                                <NumberInputField />
                              </NumberInput>
                            </FormControl>
                          </SimpleGrid>

                          <FormControl>
                            <FormLabel fontSize="xs">Text</FormLabel>
                            <AutoResizeTextarea
                              isCode
                              fontSize="sm"
                              resize="vertical"
                              minHeight="60px"
                              value={p.text || ""}
                              onChange={(e) =>
                                setPromptFieldByUid(uid, "text", e.target.value)
                              }
                            />
                          </FormControl>

                          <FormControl>
                            <FormLabel fontSize="xs">Direction</FormLabel>

                            {p.type === "field" && (
                              <Box
                                borderWidth="1px"
                                p={1}
                                mb={1}
                                borderColor={"gray.500"}
                                backgroundColor="whiteAlpha.800">

                                  <Tooltip
                                   label="Get Fields from input/output formats..."
                                   aria-label="Get Fields from input/output formats tooltip">

                                      <IconButton
                                        icon={<HiOutlineUpload />}
                                        size="xs"
                                        aria-label="Get Fields from input/output formats"


                                      />

                                   </Tooltip>

                                </Box>
                            )}

                            {p.type === "code_field" && (
                              <Box
                                borderWidth="1px"
                                p={1}
                                mb={1}
                                borderColor={"gray.500"}
                                backgroundColor="whiteAlpha.800">
<Tooltip
  label="Get Codes from input/output formats..."
  aria-label="Get Codes from input/output formats tooltip"
>
  <IconButton
    icon={<HiOutlineUpload />}
    size="xs"
    aria-label="Get Codes from input/output formats"
onClick={() => {
  // 1) Schema-first: extract code/type fields from all input/output schemas
  const pairs = collectCodeFieldsFromAllSchemas(prompts);

  const schemaText = pairs.length
    ? pairs.map(({ path, label }) => `${path} : ${label || ""}`).join("\n")
    : "";

  // 2) Then add classifications directives (from sample data), if any
  const classBlock = buildClassificationsDirectiveBlock(prompts);

  // 3) Compose final Direction text (schema first, then classifications)
  const finalText = joinBlocks(schemaText, classBlock);

  if (!finalText) {
    toast({
      title: "Nothing to insert",
      description:
        "No code/type fields detected in schemas and no classifications found in sample data.",
      status: "info",
      duration: 3500,
    });
    return;
  }

  setPromptFieldByUid(uid, "direction", finalText);

  toast({
    title: "Directions inserted",
    description: classBlock
      ? "Added schema code/type fields and classifications directives."
      : "Added schema code/type fields.",
    status: "success",
    duration: 2500,
  });
}}




  />
</Tooltip>

                                  
                                </Box>
                            )}

                            {p.type === "codeset" && (
                              <Box
                                borderWidth="1px"
                                p={1}
                                mb={1}
                                borderColor={"gray.500"}
                                backgroundColor="whiteAlpha.800">
                                <SimpleGrid columns={[1, 1, 3]} spacing={2}>
                                  <FormControl>
                                    <FormLabel fontSize="xs">Codeset</FormLabel>
                                    <Input
                                      size="xs"
                                      placeholder="e.g. A82"
                                      value={codesetInputs[uid]?.tableId || ""}
                                      onChange={(e) =>
                                        setCodesetInput(
                                          uid,
                                          "tableId",
                                          e.target.value
                                        )
                                      }
                                    />
                                  </FormControl>

                                  <FormControl>
                                    <FormLabel fontSize="xs">
                                      Codeset Filter
                                    </FormLabel>
                                    <Input
                                      size="xs"
                                      placeholder='e.g. #Placing or {"tags":["Placing"]}'
                                      value={codesetInputs[uid]?.filter || ""}
                                      onChange={(e) =>
                                        setCodesetInput(
                                          uid,
                                          "filter",
                                          e.target.value
                                        )
                                      }
                                    />
                                  </FormControl>

                                  <FormControl>
                                    <FormLabel
                                      fontSize="xs"
                                      style={{ visibility: "hidden" }}>
                                      .
                                    </FormLabel>
                                    <Tooltip
                                      label="Add Codeset values..."
                                      aria-label="Add Codeset values tooltip">
                                      <IconButton
                                        icon={<HiOutlinePlus />}
                                        size="xs"
                                        aria-label="Add Codeset values"
                                        onClick={() =>
                                          fetchCodesetDescriptiveText(
                                            uid,
                                            codesetInputs[uid]?.tableId,
                                            codesetInputs[uid]?.filter
                                          )
                                        }
                                      />
                                    </Tooltip>
                                  </FormControl>
                                </SimpleGrid>
                              </Box>
                            )}

                            <AutoResizeTextarea
                              isCode
                              fontSize="sm"
                              resize="vertical"
                              minHeight="60px"
                              value={p.direction || ""}
                              onChange={(e) =>
                                setPromptFieldByUid(
                                  uid,
                                  "direction",
                                  e.target.value
                                )
                              }
                            />
                          </FormControl>

                          <Divider my={3} />

                          <HStack justify="left" mb={2}>
                            <Heading fontSize="sm">Data Definitions</Heading>
                            <Tooltip
                              label="Add Data Definition..."
                              aria-label="Add Data Definition tooltip">
                              <IconButton
                                icon={<HiOutlinePlus />}
                                size="xs"
                                onClick={() => addPromptDataDefByUid(uid)}
                                aria-label="Add Prompt Data Definition"
                              />
                            </Tooltip>
                          </HStack>

                          {!p.dataDefinitions?.length ? (
                            <Text fontSize="sm" opacity={0.7}>
                              None
                            </Text>
                          ) : (
                            <VStack align="stretch" spacing={4}>
                              {p.dataDefinitions.map((d, ddIdx) => (
                                <Box
                                  key={`prm-${uid}-dd-${ddIdx}`}
                                  borderWidth="1px"
                                  borderRadius="md"
                                  p={3}
                                  background="whiteAlpha.700">
                                  <HStack justify="space-between" mb={2}>
                                    <Heading fontSize="xs">
                                      Data Definition #{ddIdx + 1}
                                    </Heading>
                                    <IconButton
                                      icon={<HiOutlineTrash />}
                                      size="xs"
                                      aria-label="Delete Prompt Data Definition"
                                      onClick={() => {
                                        if (
                                          window.confirm(
                                            "Delete this data definition?"
                                          )
                                        )
                                          deletePromptDataDefByUid(uid, ddIdx);
                                      }}
                                    />
                                  </HStack>

                                  <HStack spacing={3} mb={2} align="flex-end">
                                    <CodesetSelect
                                      size="xs"
                                      label="Data Standard"
                                      codesetCode="CODIFY001"
                                      value={
                                        d.dataStandard
                                          ? {
                                              code: d.dataStandard,
                                              description: "",
                                            }
                                          : null
                                      }
                                      onChange={(selected) =>
                                        handlePromptDataDefFieldByUid(
                                          uid,
                                          ddIdx,
                                          "dataStandard",
                                          selected?.code || ""
                                        )
                                      }
                                    />
                                    <CodesetSelect
                                      size="xs"
                                      label="Data Type"
                                      codesetCode="CODIFY002"
                                      value={
                                        d.dataType
                                          ? {
                                              code: d.dataType,
                                              description: "",
                                            }
                                          : null
                                      }
                                      onChange={(selected) =>
                                        handlePromptDataDefFieldByUid(
                                          uid,
                                          ddIdx,
                                          "dataType",
                                          selected?.code || ""
                                        )
                                      }
                                    />
                                  </HStack>

                                  <FormControl>
                                    <FormLabel fontSize="xs">
                                      Schema / Template
                                    </FormLabel>
                                    {(p.type === "input_format" ||
                                      p.type === "output_format") && (
                                      <Box
                                        borderWidth="1px"
                                        p={2}
                                        mb={2}
                                        borderColor={"gray.500"}
                                        backgroundColor="whiteAlpha.800"
                                        borderRadius="md">
                                        <SimpleGrid
                                          columns={[1, 1, 3]}
                                          spacing={2}>
                                          <FormControl
                                            isDisabled={registersLoading}>
                                            <FormLabel fontSize="xs">
                                              Register
                                            </FormLabel>
                                            <Select
                                              size="xs"
                                              placeholder={
                                                registersLoading
                                                  ? "Loading registers..."
                                                  : registersError
                                                  ? "Failed to load"
                                                  : "Select a register"
                                              }
                                              value={
                                                (
                                                  registerSelections[
                                                    keyFor(uid, ddIdx)
                                                  ] || {}
                                                ).registerId || ""
                                              }
                                              onChange={(e) => {
                                                setRegisterSelection(
                                                  uid,
                                                  ddIdx,
                                                  "registerId",
                                                  e.target.value
                                                );
                                                // reset entry when register changes
                                                setRegisterSelection(
                                                  uid,
                                                  ddIdx,
                                                  "entryId",
                                                  ""
                                                );
                                              }}>
                                              {registers.map((r) => (
                                                <option
                                                  key={r._id}
                                                  value={r._id}>
                                                  {r.name || r.type || r._id}
                                                </option>
                                              ))}
                                            </Select>
                                          </FormControl>

                                          <FormControl
                                            isDisabled={registersLoading}>
                                            <FormLabel fontSize="xs">
                                              Register Entry
                                            </FormLabel>
                                            <Select
                                              size="xs"
                                              placeholder="Select an entry"
                                              value={
                                                (
                                                  registerSelections[
                                                    keyFor(uid, ddIdx)
                                                  ] || {}
                                                ).entryId || ""
                                              }
                                              onChange={(e) =>
                                                setRegisterSelection(
                                                  uid,
                                                  ddIdx,
                                                  "entryId",
                                                  e.target.value
                                                )
                                              }>
                                              {(
                                                registers.find(
                                                  (r) =>
                                                    r._id ===
                                                    (
                                                      registerSelections[
                                                        keyFor(uid, ddIdx)
                                                      ] || {}
                                                    ).registerId
                                                )?.entries || []
                                              ).map((ent) => (
                                                <option
                                                  key={ent._id}
                                                  value={ent._id}>
                                                  {ent.name || ent._id}
                                                </option>
                                              ))}
                                            </Select>
                                          </FormControl>

                                          <FormControl>
                                            <FormLabel
                                              fontSize="xs"
                                              style={{ visibility: "hidden" }}>
                                              .
                                            </FormLabel>
                                            <Tooltip
                                              label="Insert template into Schema"
                                              aria-label="Insert template">
                                              <IconButton
                                                icon={<HiOutlineUpload />}
                                                size="xs"
                                                aria-label="Insert template"
                                                onClick={() =>
                                                  insertRegisterTemplate(
                                                    uid,
                                                    ddIdx
                                                  )
                                                }
                                                isDisabled={registersLoading}
                                              />
                                            </Tooltip>
                                          </FormControl>
                                        </SimpleGrid>
                                      </Box>
                                    )}

                                    <AutoResizeTextarea
                                      isCode
                                      fontSize="xs"
                                      resize="vertical"
                                      minHeight="120px"
                                      value={d.schema || ""}
                                      onChange={(e) =>
                                        handlePromptDataDefFieldByUid(
                                          uid,
                                          ddIdx,
                                          "schema",
                                          e.target.value
                                        )
                                      }
                                    />
                                    <HStack mt={2} spacing={2}>
                                      <Button
                                        size="xs"
                                        onClick={() =>
                                          handlePromptDataDefFieldByUid(
                                            uid,
                                            ddIdx,
                                            "schema",
                                            prettyJson(d.schema || "")
                                          )
                                        }>
                                        Prettify JSON
                                      </Button>
                                      <Button
                                        size="xs"
                                        variant="outline"
                                        onClick={() => {
                                          const s = safeString(d.schema).trim();
                                          if (!s) {
                                            toast({
                                              title: "Schema is empty",
                                              status: "info",
                                              duration: 2000,
                                            });
                                            return;
                                          }
                                          if (
                                            s.startsWith("{") ||
                                            s.startsWith("[")
                                          ) {
                                            const [, err] = tryParseJson(s);
                                            if (err) {
                                              toast({
                                                title: "Invalid JSON",
                                                description: err.message,
                                                status: "error",
                                                duration: 4000,
                                              });
                                            } else {
                                              toast({
                                                title: "Valid JSON",
                                                status: "success",
                                                duration: 1500,
                                              });
                                            }
                                          } else {
                                            toast({
                                              title: "Not JSON",
                                              description:
                                                "Schema does not appear to be JSON (that’s fine if you intend free-form).",
                                              status: "info",
                                              duration: 2500,
                                            });
                                          }
                                        }}>
                                        Validate
                                      </Button>
                                    </HStack>
                                  </FormControl>

                                  <FormControl mt={2}>
                                    <FormLabel fontSize="xs">
                                      Sample Data (optional)
                                    </FormLabel>
                                    <AutoResizeTextarea
                                      isCode
                                      fontSize="xs"
                                      resize="vertical"
                                      minHeight="80px"
                                      value={d.data || ""}
                                      onChange={(e) =>
                                        handlePromptDataDefFieldByUid(
                                          uid,
                                          ddIdx,
                                          "data",
                                          e.target.value
                                        )
                                      }
                                    />
                                  </FormControl>
                                </Box>
                              ))}
                            </VStack>
                          )}
                        </TabPanel>
                      );
                    })}
                  </TabPanels>
                </Tabs>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default CodifyDefinitionsEditor;
